import { supabase } from '@/shared/supabase/client'
import { isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'
import { screenshotFixtureShiftLocationPeriodPayments } from '@/shared/screenshotSeed/synapScreenshotFixtures'
import { toRepositoryError } from '@/shared/repositories/common/repositoryError'
import type { Database } from '@/shared/supabase/database.types'

export type ShiftLocationPeriodPaymentRecord = {
  id: string
  ownerUserId: string
  locationId: string
  periodKey: string
  periodStart: string
  periodEnd: string
  paid: boolean
  paidAt: string | null
  createdAt: string
  updatedAt: string
}

type ShiftLocationPeriodPaymentRow =
  Database['public']['Tables']['shift_location_period_payments']['Row']

const getCurrentUserId = async (): Promise<string> => {
  const { data, error } = await supabase.auth.getUser()
  if (error) throw toRepositoryError(error, 'Não foi possível validar a sessão.')
  if (!data.user) throw toRepositoryError(new Error('Usuário não autenticado.'), 'Usuário não autenticado.', 401)
  return data.user.id
}

const toRecord = (row: ShiftLocationPeriodPaymentRow): ShiftLocationPeriodPaymentRecord => ({
  id: row.id,
  ownerUserId: row.owner_user_id,
  locationId: row.location_id,
  periodKey: row.period_key,
  periodStart: row.period_start,
  periodEnd: row.period_end,
  paid: row.paid,
  paidAt: row.paid_at,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
})

export const shiftLocationPeriodPaymentsRepository = {
  listForOwnerInKeys: async (
    keys: string[],
  ): Promise<{ payments: ShiftLocationPeriodPaymentRecord[] }> => {
    if (isScreenshotFixtureMode()) {
      return { payments: screenshotFixtureShiftLocationPeriodPayments(keys) }
    }
    if (keys.length === 0) {
      return { payments: [] }
    }
    const userId = await getCurrentUserId()
    const { data, error } = await supabase
      .from('shift_location_period_payments')
      .select('*')
      .eq('owner_user_id', userId)
      .in('period_key', keys)

    if (error) throw toRepositoryError(error, 'Não foi possível listar o status de pagamento.')
    return { payments: (data ?? []).map(toRecord) }
  },

  upsertPaid: async (payload: {
    locationId: string
    periodKey: string
    periodStart: string
    periodEnd: string
    paid: boolean
  }): Promise<{ payment: ShiftLocationPeriodPaymentRecord | null }> => {
    if (isScreenshotFixtureMode()) {
      return { payment: null }
    }
    const userId = await getCurrentUserId()
    const paidAt = payload.paid ? new Date().toISOString() : null
    const { data, error } = await supabase
      .from('shift_location_period_payments')
      .upsert(
        {
          owner_user_id: userId,
          location_id: payload.locationId,
          period_key: payload.periodKey,
          period_start: payload.periodStart,
          period_end: payload.periodEnd,
          paid: payload.paid,
          paid_at: paidAt,
        },
        { onConflict: 'owner_user_id,location_id,period_key' },
      )
      .select('*')
      .single()

    if (error) throw toRepositoryError(error, 'Não foi possível salvar o status de pagamento.')
    return { payment: toRecord(data) }
  },
}
